import hash from 'hash.js';
import Cookies from 'js-cookie';
import { get, isMatch, isNil, omitBy } from 'lodash';

import { TRACKING_COOKIES, USER_STATES, USER_SESSION_STATE } from 'state/constants';
import DataDogLogs from 'utils/DataDogLogs';
import DataLayer from 'utils/DataLayer';
import { encryptEmail } from 'utils/JSEncrypt';
import SessionStorage from 'utils/SessionStorage';
import Settings from 'utils/Settings';
import { getCurrentDomain, parseQueryString } from 'utils/Url';

import {
  ITERABLE_EMAIL_COOKIE,
  ITERABLE_EMAIL_ALLBIRDS_ID_QUERY_PARAM,
  ITERABLE_EMAIL_CAMPAIGN_ID_QUERY_PARAM,
  ITERABLE_EMAIL_TEMPLATE_ID_QUERY_PARAM,
  ITERABLE_EMAIL_USER_ID_QUERY_PARAM,
} from './constants';

const Customer = class {
  // legacy cookie, unused
  static get email() {
    return Cookies.get(TRACKING_COOKIES.ALLBIRDS_EMAIL);
  }

  static get encryptedEmail() {
    return Cookies.get(TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL);
  }

  static get userState() {
    return Cookies.get(TRACKING_COOKIES.USER_STATE);
  }

  static get dynamicYieldServerID() {
    return Cookies.get(TRACKING_COOKIES.DYNAMIC_YIELD_SERVER);
  }

  static get dynamicYieldClientID() {
    return Cookies.get(TRACKING_COOKIES.DYNAMIC_YIELD);
  }

  static get allbirdsId() {
    return Cookies.get(TRACKING_COOKIES.ALLBIRDS_ID);
  }

  static get userId() {
    return Cookies.get(TRACKING_COOKIES.USER_ID);
  }

  static get iterableCampaignId() {
    return Cookies.get(TRACKING_COOKIES.ITERABLE_CAMPAIGN_ID);
  }

  static get iterableTemplateId() {
    return Cookies.get(TRACKING_COOKIES.ITERABLE_TEMPLATE_ID);
  }

  static get preferredStore() {
    return Cookies.get(TRACKING_COOKIES.PREFERRED_STORE);
  }

  static get anonymousId() {
    return window.rudderanalytics?.getAnonymousId();
  }

  static get currentDomain() {
    return getCurrentDomain();
  }

  static get sessionReplaySessionId() {
    return Cookies.get(TRACKING_COOKIES.SESSION_REPLAY_SESSION_ID);
  }

  static set sessionReplaySessionId(sessionId) {
    Cookies.set(TRACKING_COOKIES.SESSION_REPLAY_SESSION_ID, sessionId);
  }

  static get getDefaultUserTraits() {
    return {
      dynamic_yield_session_id: Customer.dynamicYieldClientID,
      dynamic_yield_server_id: Customer.dynamicYieldServerID,
      anonymousId: Customer.anonymousId,
      auth_state: Customer.userState,
      shopify_domain: window.location.host,
      allbirds_id: Customer.allbirdsId,
      user_id: Customer.userId,
      session_id: DataDogLogs.sessionId,
      session_replay_session_id: Customer.sessionReplaySessionId,
      screen_height: window?.screen?.height,
      screen_width: window?.screen?.width,
    };
  }

  static getUserTraits({ email, additionalTraits = {} }) {
    let supplementaryTraits = {};

    if (email) {
      const hashedEmail = hash.sha256().update(email).digest('hex');

      supplementaryTraits = {
        email: Customer.encryptedEmail || encryptEmail(email),
        is_using_encrypted_email: true,
        hashed_email: hashedEmail,
      };
    }

    return omitBy(
      {
        ...Customer.getDefaultUserTraits,
        ...supplementaryTraits,
        ...additionalTraits,
      },
      isNil
    );
  }

  static shouldUpdateUser(existingUserTraits, proposedUserTraits, email) {
    // update user if either
    // 1. email was captured and not already identified
    // 2. proposed traits are NOT subset of existing

    if (!email) {
      return !isMatch(existingUserTraits, proposedUserTraits);
    }
    // these emails are encrypted, we want to pull them out
    // non-deterministic due to salting means we can't compare them
    const { email: ignored, ...existingUserTraitsWithoutEncyrptedEmail } = existingUserTraits;
    const { email: Ignored, ...proposedUserTraitsWithoutEncyrptedEmail } = proposedUserTraits;

    // checking for subset here. Not all attributes present in existing may be in proposed
    // accepts_marketing for example are only present in marketing sign up actions
    // this will not overwrite if default attributes are still the same
    return !isMatch(
      existingUserTraitsWithoutEncyrptedEmail,
      proposedUserTraitsWithoutEncyrptedEmail
    );
  }

  // legacy cookie, unused
  static setUserEmail(email) {
    Cookies.set(TRACKING_COOKIES.ALLBIRDS_EMAIL, email, {
      domain: Customer.currentDomain,
      expires: 365.25,
    });
  }

  static setUserEncryptedEmail(email) {
    Cookies.set(TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL, email, {
      domain: Customer.currentDomain,
      expires: 365.25,
    });
  }

  static setUserState(userState) {
    Cookies.set(TRACKING_COOKIES.USER_STATE, userState, {
      domain: Customer.currentDomain,
      expires: 365.25,
    });
  }

  static setUserAllbirdsId(allbirdsId) {
    return Cookies.set(TRACKING_COOKIES.ALLBIRDS_ID, allbirdsId, {
      domain: Customer.currentDomain,
      expires: 365.25,
    });
  }

  static setUserId(userId) {
    return Cookies.set(TRACKING_COOKIES.USER_ID, userId, {
      domain: Customer.currentDomain,
      expires: 365,
    });
  }

  static setPreferredStore(storeHandle) {
    Cookies.set(TRACKING_COOKIES.PREFERRED_STORE, storeHandle, {
      // no domain right now due to bug in dev stores
      // re-enable if needed on .checkout or .stores
      // domain: Customer.currentDomain,
      expires: 365.25,
    });
  }

  static tryGetAllbirdsIdFromUrl() {
    return get(parseQueryString(), ITERABLE_EMAIL_ALLBIRDS_ID_QUERY_PARAM);
  }

  static trySetAllbirdsIdFromUrl() {
    const customerAllbirdsId = Customer.tryGetAllbirdsIdFromUrl();
    if (customerAllbirdsId) {
      Customer.setUserAllbirdsId(customerAllbirdsId);
    }
  }

  static tryGetUserIdFromUrl() {
    return get(parseQueryString(), ITERABLE_EMAIL_USER_ID_QUERY_PARAM);
  }

  static trySetUserIdFromUrl() {
    const userId = Customer.tryGetUserIdFromUrl();
    if (userId) {
      Customer.setUserId(userId);
    }
  }

  static setIterableCampaignId(campaignId) {
    return Cookies.set(TRACKING_COOKIES.ITERABLE_CAMPAIGN_ID, campaignId, {
      domain: Customer.currentDomain,
      expires: 1,
    });
  }

  static setIterableTemplateId(templateId) {
    return Cookies.set(TRACKING_COOKIES.ITERABLE_TEMPLATE_ID, templateId, {
      domain: Customer.currentDomain,
      expires: 1,
    });
  }

  static tryGetIterableCampaignIdFromUrl() {
    return get(parseQueryString(), ITERABLE_EMAIL_CAMPAIGN_ID_QUERY_PARAM);
  }

  static tryGetIterableTemplateIdFromUrl() {
    return get(parseQueryString(), ITERABLE_EMAIL_TEMPLATE_ID_QUERY_PARAM);
  }

  static trySetIterableRevenueTrackingIdsFromUrl() {
    const campaignId = Customer.tryGetIterableCampaignIdFromUrl();
    const templateId = Customer.tryGetIterableTemplateIdFromUrl();
    if (campaignId && templateId) {
      Customer.setIterableCampaignId(campaignId);
      Customer.setIterableTemplateId(templateId);
    }
  }

  static clearUser() {
    Cookies.remove(TRACKING_COOKIES.ALLBIRDS_EMAIL);
    Cookies.remove(TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL);
    Cookies.remove(TRACKING_COOKIES.ALLBIRDS_ID);
    Cookies.remove(TRACKING_COOKIES.USER_ID);
    Cookies.remove(TRACKING_COOKIES.ITERABLE_CAMPAIGN_ID);
    Cookies.remove(TRACKING_COOKIES.ITERABLE_TEMPLATE_ID);
    Customer.setUserState(USER_STATES.ANONYMOUS);
  }

  static identifyActionEmail(email, userState) {
    const currentUserState = Customer.userState;
    const encryptedEmail = encryptEmail(email);

    if (userState === USER_STATES.LOGGED_IN) {
      Customer.setUserState(USER_STATES.LOGGED_IN);
      Customer.setUserEncryptedEmail(encryptedEmail);
    } else if (currentUserState !== USER_STATES.LOGGED_IN) {
      Customer.setUserState(USER_STATES.IDENTIFIED);
      Customer.setUserEncryptedEmail(encryptedEmail);
    }
  }

  static identifyPageloadEmail() {
    const currentUserState = Customer.userState;
    const currentEmail = Customer.encryptedEmail;
    const iterableEmailCookie = Cookies.get(ITERABLE_EMAIL_COOKIE);
    const shopifyLoggedIn = Settings.customerAccountEmail;

    let updatedUserState;

    if (shopifyLoggedIn) {
      updatedUserState = USER_STATES.LOGGED_IN;

      const encryptedShopifyEmail = encryptEmail(shopifyLoggedIn);

      Customer.setUserEncryptedEmail(encryptedShopifyEmail);
    } else if (currentUserState === USER_STATES.IDENTIFIED || currentEmail) {
      updatedUserState = USER_STATES.IDENTIFIED;
    } else if (iterableEmailCookie) {
      updatedUserState = USER_STATES.IDENTIFIED;

      const encryptedIterableEmail = encryptEmail(iterableEmailCookie);

      Customer.setUserEncryptedEmail(encryptedIterableEmail);
    } else {
      updatedUserState = USER_STATES.ANONYMOUS;
    }

    Customer.setUserState(updatedUserState);
    Customer.trySetAllbirdsIdFromUrl();
    Customer.trySetUserIdFromUrl();
    Customer.trySetIterableRevenueTrackingIdsFromUrl();

    DataLayer.pushEvent({
      event: 'pageData',
      attributes: {
        userState: updatedUserState,
      },
    });
  }

  static setSmsMarketingOptIn(status) {
    SessionStorage.setSessionStorageItem(
      USER_SESSION_STATE.ALLBIRDS_SMS_MARKETING_OPT_IN,
      !!status
    );
  }

  static getSmsMarketingOptIn() {
    return !!SessionStorage.getSessionStorageItem(USER_SESSION_STATE.ALLBIRDS_SMS_MARKETING_OPT_IN);
  }
};

export default Customer;
